import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const PAGE_SIZE = 25;

/**
 * Reusable pagination component.
 *
 * Usage:
 *   const { page, setPage, pageCount, paged } = usePagination(filteredItems);
 *   // Render `paged` items, then <Pagination page={page} pageCount={pageCount} onChange={setPage} />
 */
export function usePagination(items, perPage = PAGE_SIZE) {
  const [page, setPage] = React.useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / perPage));

  // Clamp page when items change (e.g., filters reduce results)
  React.useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [pageCount, page]);

  const start = (page - 1) * perPage;
  const paged = items.slice(start, start + perPage);

  return { page, setPage, pageCount, paged, total: items.length, perPage, start };
}

export default function Pagination({ page, pageCount, onChange, total, perPage = PAGE_SIZE }) {
  if (pageCount <= 1) return null;

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  // Build visible page numbers (max 5 around current)
  const pages = [];
  let lo = Math.max(1, page - 2);
  let hi = Math.min(pageCount, page + 2);
  if (hi - lo < 4) {
    if (lo === 1) hi = Math.min(pageCount, lo + 4);
    else lo = Math.max(1, hi - 4);
  }
  for (let i = lo; i <= hi; i++) pages.push(i);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
      {/* Info */}
      <p className="text-xs text-slate-400 order-2 sm:order-1">
        Showing <span className="font-semibold text-slate-600">{start}–{end}</span> of{" "}
        <span className="font-semibold text-slate-600">{total}</span>
      </p>

      {/* Controls */}
      <div className="flex items-center gap-1 order-1 sm:order-2">
        <button
          onClick={() => onChange(1)}
          disabled={page === 1}
          className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="First page"
        >
          <ChevronsLeft className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Previous"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
        </button>

        {lo > 1 && <span className="text-xs text-slate-400 px-1">…</span>}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`min-w-[28px] h-7 rounded-md text-xs font-semibold transition-colors ${
              p === page
                ? "bg-brand-primary text-white shadow-sm"
                : "text-slate-500 hover:bg-slate-200"
            }`}
          >
            {p}
          </button>
        ))}

        {hi < pageCount && <span className="text-xs text-slate-400 px-1">…</span>}

        <button
          onClick={() => onChange(page + 1)}
          disabled={page === pageCount}
          className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Next"
        >
          <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <button
          onClick={() => onChange(pageCount)}
          disabled={page === pageCount}
          className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Last page"
        >
          <ChevronsRight className="w-3.5 h-3.5 text-slate-500" />
        </button>
      </div>
    </div>
  );
}
